import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { t } from "@/lib/i18n";
import { usePublicLang } from "@/lib/publicLang";

/**
 * Static "what's been shipped" page. Hand-curated rather than parsed from
 * CHANGELOG.md so we control the public framing (no internal jargon, no
 * compliance language a lawyer hasn't cleared yet).
 *
 * When something new ships, just prepend a row at the top of UPDATES.
 */
const UPDATES = [
  {
    date: "July 2026",
    title: "A demo you can actually play with — and it looks like the real thing",
    body: "The home page now shows the real board your child would hold: a full 6×8 talking board on one screen, tiles that never move. Tap any tile to hear it, build a sentence, and press Speak. The \"your photo\" tiles are exactly where your family's own photos and your recorded voice go in the app.",
  },
  {
    date: "July 2026",
    title: "Build a starter board by answering two questions",
    body: "Tell us your child's age and how many words they use today, and a starter board comes together in seconds — the right words for where they are, with room to grow. No setup, and no account needed just to try it.",
  },
  {
    date: "July 2026",
    title: "Animated helpers on the tricky words",
    body: "Words like hi, bye, stop, and help now have a friendly little character that waves, puts a hand out, or reaches — a gentle cue for kids who read a gesture faster than a word. It calms down automatically if your device prefers reduced motion.",
  },
  {
    date: "July 2026",
    title: "Invite a therapist or school — you approve every change",
    body: "Therapists and school teams can connect by invite and suggest changes, or send a ready-made classroom board. Nothing ever lands on your child's device unless you accept it and send it there yourself. You hold every key.",
  },
  {
    date: "May 2026",
    title: "Outside the US? Join the international waitlist",
    body: "We added an in-app waitlist for visitors outside the United States. Pick your country, drop your email, and we'll let you know the moment SonSpeak opens there. Top-bar language picker (EN / ES / FR) lands at the same time so the message reaches you in your language.",
  },
  {
    date: "May 2026",
    title: "Built-in school-day structure",
    body: "Tiles can now be marked Anytime, Home only, or School only — and SonSpeak quietly tucks distracting tiles away during school hours (Mon–Fri 8a–3p, parent-adjustable). Big calm cue at the top of the board lets your kid know school mode is on.",
  },
  {
    date: "May 2026",
    title: "Helper invites by email + activity log",
    body: "Inviting a grandparent, therapist, or school staff now sends a one-tap accept email. Helpers get their own list of every tile change they've suggested with current decision status — pending parent, pending clinical, approved, rejected.",
  },
  {
    date: "May 2026",
    title: "Family Notes — one-way parent → child banners",
    body: "Send a calm, single-message banner that lands on your child's board mid-school-day. Eight quick presets (\"I love you\", \"Pickup at 3\", \"Big hug\"…), no replies, no chat thread — sacred channel.",
  },
  {
    date: "May 2026",
    title: "Privacy-first design, end-to-end",
    body: "Privacy Dashboard at /privacy/me lets you see every helper, audit every action, export your child's communication history, and delete everything in one tap. No marketing trackers, no third-party data sale.",
  },
  {
    date: "May 2026",
    title: "Tier upgrade prompts that don't blame the user",
    body: "Hit a members-only feature? You see a calm in-context modal explaining what Membership includes — never a hard wall, no broken page, no error toast.",
  },
  {
    date: "April 2026",
    title: "Therapist multi-client dashboard",
    body: "Speech therapists can manage every client board from one screen, push share-links in batch, and export shareable CSV/PDF usage snapshots for caregiver conversations.",
  },
];

export default function WhatsNew() {
  const [lang] = usePublicLang();
  useSEO({
    title: t(lang, "public.whatsnew.title"),
    description: t(lang, "public.whatsnew.subtitle"),
    path: "/whats-new",
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="pill-btn mb-6 inline-flex" data-testid="whatsnew-back">
          <ArrowLeft size={14} /> Home
        </Link>

        <div className="mb-8 flex items-start gap-3">
          <div className="brutal-card bg-[color:var(--gold)] p-3 shrink-0">
            <Sparkles size={20} aria-hidden />
          </div>
          <div>
            <h1 className="display text-3xl md:text-4xl font-extrabold mb-1" data-testid="whatsnew-title">
              {t(lang, "public.whatsnew.title")}
            </h1>
            <p className="text-[color:var(--ink-soft)]">{t(lang, "public.whatsnew.subtitle")}</p>
          </div>
        </div>

        <ol className="space-y-4">
          {UPDATES.map((u, idx) => (
            <li
              key={idx}
              className="brutal-card bg-white p-5"
              data-testid={`whatsnew-item-${idx}`}
            >
              <div className="display text-xs uppercase tracking-[0.2em] text-[color:var(--coral-deep)] font-bold mb-1">
                {u.date}
              </div>
              <h2 className="display text-lg font-bold mb-2">{u.title}</h2>
              <p className="text-[color:var(--ink-soft)] leading-relaxed">{u.body}</p>
            </li>
          ))}
        </ol>

        <div className="mt-10 text-center text-xs text-[color:var(--ink-soft)]">
          Built piece by piece by a parent who needed it. <Link to="/built-in-a-day" className="underline">See the build story</Link>.
        </div>
      </div>
    </div>
  );
}
